import {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import { AgGridReact } from 'ag-grid-react';
import { useSearchParams } from 'react-router-dom';

import { WithLocalStorage } from '@/components';
import { useDefaultGridOptions } from '@/grid';
import { defaultColDef } from '@/grid/defaults';
import { PrimaryContext } from '@/services';
import { AspirationsToday } from '@/types.gql';
import { useContextMenu } from '@/utils/context-menu';

import { useOpuSearchContext } from '../../hooks/useOpuContext';
import { useColumnDefs } from '../../utils/columnDefs';

export type AspirationGridProps = {
  className?: string;
  setShowForm: (value: boolean) => void;
  aspirations: AspirationsToday[];
  originalTimeZone: string | null;
  setOriginalTimeZone: Dispatch<SetStateAction<string | null>>;
};

export const OpuSearchGrid = ({
  className,
  setShowForm,
  aspirations,
  originalTimeZone,
  setOriginalTimeZone,
}: AspirationGridProps) => {
  const { themeMode } = useContext(PrimaryContext);
  const defaultGridOptions = useDefaultGridOptions();
  const columnDefs = useColumnDefs();
  const { setSelectedAspiration } = useOpuSearchContext();
  const [params] = useSearchParams();
  const gridRef = useRef<AgGridReact>(null);

  const aspirationId = params.get('id');
  const timeZone = params.get('tz') || '';
  const urlDate = params.get('date') || ''; // yyyy-MM-dd expected

  const [gridAspirations, setGridAspirations] = useState<AspirationsToday[]>();
  const [isFirstTimeZoneSelection, setIsFirstTimeZoneSelection] =
    useState(true);

  useEffect(() => {
    if (aspirationId && aspirations) {
      const matchingAspiration = aspirations.find(
        (asp) => asp.aspirationId === aspirationId,
      );
      if (matchingAspiration) {
        setSelectedAspiration(matchingAspiration);
        setShowForm(true);
      }
    }
  }, [aspirationId, aspirations, setSelectedAspiration, setShowForm]);

  useEffect(() => {
    if (!aspirationId) setShowForm(false);
  }, [aspirationId, params, setShowForm]);

  const parseHHmm = (hhmm?: string | null) => {
    if (!hhmm || !/^\d{4}$/.test(hhmm)) return null;
    const h = Number(hhmm.slice(0, 2));
    const m = Number(hhmm.slice(2, 4));
    if (Number.isNaN(h) || Number.isNaN(m)) return null;
    return { h, m };
  };

  const formatInTZ_HHmm = (date: Date, tz: string): string => {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: tz,
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).formatToParts(date);
    const hh = parts.find((p) => p.type === 'hour')?.value ?? '00';
    const mm = parts.find((p) => p.type === 'minute')?.value ?? '00';
    return `${hh}${mm}`;
  };

  const convertWallTimeBetweenTZ = (
    dateStr: string,
    hhmm: string,
    fromTz: string,
    toTz: string,
  ): string => {
    const parsed = parseHHmm(hhmm);
    if (!parsed) return hhmm;

    const ymd = /^\d{4}-\d{2}-\d{2}$/.test(dateStr)
      ? dateStr
      : new Date().toISOString().slice(0, 10);

    let guess = new Date(
      Date.UTC(
        Number(ymd.slice(0, 4)),
        Number(ymd.slice(5, 7)) - 1,
        Number(ymd.slice(8, 10)),
        parsed.h,
        parsed.m,
        0,
        0,
      ),
    );

    const fmtFrom = new Intl.DateTimeFormat('en-GB', {
      timeZone: fromTz,
      hour12: false,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });

    const desiredMinutes = parsed.h * 60 + parsed.m;

    for (let i = 0; i < 3; i++) {
      const parts = fmtFrom.formatToParts(guess);
      const gotH = Number(parts.find((p) => p.type === 'hour')?.value ?? '0');
      const gotM = Number(parts.find((p) => p.type === 'minute')?.value ?? '0');
      const gotMinutes = gotH * 60 + gotM;
      const deltaMin = desiredMinutes - gotMinutes;
      if (deltaMin === 0) break;
      guess = new Date(guess.getTime() + deltaMin * 60_000);
    }

    try {
      return formatInTZ_HHmm(guess, toTz);
    } catch {
      return hhmm;
    }
  };

  const convertAspirationTimes = (
    rows: AspirationsToday[],
    fromTz: string,
    toTz: string,
    dateStr: string,
  ): AspirationsToday[] => {
    if (!rows?.length) return rows;
    return rows.map((r) => {
      const current = (r as any).aspirationTime as string | undefined;
      if (!current) return r;
      try {
        const next = convertWallTimeBetweenTZ(dateStr, current, fromTz, toTz);
        return next === current
          ? r
          : ({ ...r, aspirationTime: next } as AspirationsToday);
      } catch {
        return r;
      }
    });
  };

  useEffect(() => {
    if (!aspirations) return;

    if (timeZone === '') {
      setGridAspirations(aspirations);
      return;
    }

    if (isFirstTimeZoneSelection) {
      setOriginalTimeZone(timeZone);
      setGridAspirations(aspirations);
      setIsFirstTimeZoneSelection(false);
      return;
    }

    if (originalTimeZone && originalTimeZone !== timeZone) {
      const updated = convertAspirationTimes(
        aspirations,
        originalTimeZone,
        timeZone,
        urlDate,
      );
      setGridAspirations(updated);
    } else {
      setGridAspirations(aspirations);
    }
  }, [
    timeZone,
    aspirations,
    isFirstTimeZoneSelection,
    originalTimeZone,
    setOriginalTimeZone,
    urlDate,
  ]);

  const { getContextMenuItems } = useContextMenu({ idKey: 'aspirationId' });

  return (
    <WithLocalStorage storageKey="OpuSearch-grid">
      <AgGridReact
        ref={gridRef}
        className={`ag-theme-transova${themeMode === 'dark' ? '-dark' : ''} ${className}`.trim()}
        rowData={gridAspirations || []}
        defaultColDef={defaultColDef}
        columnDefs={columnDefs}
        overlayLoadingTemplate="Please Select a Site and a Technician"
        rowSelection="single"
        getRowId={(params) => params.data.aspirationId}
        gridOptions={defaultGridOptions}
        context={{ setShowForm }}
        getContextMenuItems={getContextMenuItems}
      />
    </WithLocalStorage>
  );
};
